/**
 * WorkflowSetupPage — admin list of every workflow on the system.
 *
 * One editor: every row opens the Stage Editor (/workflow/setup/:id/stages),
 * which owns settings, stages, owners, SLAs, gates, escalations and spawns.
 * The A4 guided wizard was removed Sep 23 — it re-saved transitions without
 * legacy required_fields gates and silently wiped them.
 *
 * Workflow Health: every row carries badges for broken paths, steps with no
 * responsible party, and steps with no SLA (server/services/workflowHealth.js).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';

const COLORS = {
  brand: '#3F66A0', brandTint: '#eef3fb',
  border: '#e2e8f0', ink: '#0f172a', inkSoft: '#475569', inkMuted: '#94a3b8',
  red: '#dc2626', greenSoft: '#10b981',
  amberTint: '#fef3c7', amberBorder: '#fcd34d',
};

// One style per health check. Order here is the order badges render.
const HEALTH_KINDS = [
  { kind: 'path',  one: 'broken path',   many: 'broken paths',   bg: '#fee2e2', fg: '#991b1b' },
  { kind: 'owner', one: 'missing owner', many: 'missing owners', bg: '#fef3c7', fg: '#92400e' },
  { kind: 'sla',   one: 'missing SLA',   many: 'missing SLAs',   bg: '#e0e7ff', fg: '#3730a3' },
];
const countLabel = (k, n) => `${n} ${n === 1 ? k.one : k.many}`;
const issueTotal = (h) => (h ? h.counts.path + h.counts.owner + h.counts.sla : 0);

export default function WorkflowSetupPage({ moduleFilter } = {}) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  // workflow_id -> health entry. Null until loaded (or if the check fails —
  // the list still works without it).
  const [health, setHealth] = useState(null);
  const [problemsOnly, setProblemsOnly] = useState(false);

  const loadTemplates = useCallback(async () => {
    const params = { active_only: 'false' };
    if (moduleFilter) params.module = moduleFilter;
    try {
      const data = await api.getWorkflowTemplates(params);
      setTemplates(data || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
    try {
      const h = await api.getWorkflowHealth(moduleFilter ? { module: moduleFilter } : undefined);
      setHealth(new Map((h || []).map(e => [e.workflow_id, e])));
    } catch (err) {
      console.error('Failed to load workflow health:', err);
    }
  }, [moduleFilter]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // One-page path: create a draft with 3 starter stages wired in a line,
  // then land directly in the editor.
  const [creatingDraft, setCreatingDraft] = useState(false);
  const createInEditor = async () => {
    const name = window.prompt('Name the new workflow:');
    if (!name || !name.trim()) return;
    setCreatingDraft(true);
    try {
      const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64) || 'workflow';
      const tpl = await api.createWorkflowTemplate({
        name: name.trim(),
        trigger_source_type: `custom.${slug}`,
        is_active: true,
      });
      const saved = await api.saveWorkflowStages(tpl.id, [
        { name: 'Submitted', color: '#3F66A0', position: 0, stage_type: 'start', owner_routing: 'role' },
        { name: 'In Review', color: '#f59e0b', position: 1, stage_type: 'active', sla_hours: 48, owner_routing: 'role' },
        { name: 'Done', color: '#10b981', position: 2, stage_type: 'terminal', owner_routing: 'role' },
      ]);
      if (Array.isArray(saved) && saved.length === 3) {
        await api.saveWorkflowTransitions(tpl.id, [
          { from_stage_id: saved[0].id, to_stage_id: saved[1].id, required_fields: null },
          { from_stage_id: saved[1].id, to_stage_id: saved[2].id, required_fields: null },
        ]);
      }
      navigate(`/workflow/setup/${tpl.id}/stages`);
    } catch (err) {
      alert(err.message || 'Failed to create workflow');
    } finally {
      setCreatingDraft(false);
    }
  };
  const openEdit = (tmpl) => navigate(`/workflow/setup/${tmpl.id}/stages`);

  const toggleActive = async (tmpl) => {
    try {
      await api.updateWorkflowTemplate(tmpl.id, { is_active: !tmpl.is_active });
      await loadTemplates();
    } catch (err) {
      alert(err.message || 'Toggle failed');
    }
  };

  // Summary counts only active workflows — a disabled one can't stall work.
  const activeHealth = health
    ? templates.filter(t => t.is_active).map(t => health.get(t.id)).filter(Boolean)
    : [];
  const needAttention = activeHealth.filter(h => issueTotal(h) > 0).length;
  const totals = HEALTH_KINDS.map(k => ({
    ...k, n: activeHealth.reduce((sum, h) => sum + h.counts[k.kind], 0),
  }));
  const shown = problemsOnly && health
    ? templates.filter(t => issueTotal(health.get(t.id)) > 0)
    : templates;

  return (
    <div style={{ padding: '28px clamp(16px, 4vw, 32px) 48px', maxWidth: 1080, margin: '0 auto' }}>
      <header style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap', marginBottom: 18,
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: COLORS.ink, letterSpacing: '-0.02em' }}>
            Workflow Setup
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: COLORS.inkMuted }}>
            Every workflow on the system. Click any to edit its settings, stages, owners, and SLAs.
          </p>
        </div>
        <button type="button" onClick={createInEditor} disabled={creatingDraft}
          style={{
            padding: '10px 22px', borderRadius: 10, border: 'none',
            background: '#D4A843', color: '#1a1a2e', fontSize: 14, fontWeight: 700,
            cursor: 'pointer', boxShadow: '0 2px 6px rgba(212,168,67,0.3)',
          }}>
          {creatingDraft ? 'Creating…' : '+ New Workflow'}
        </button>
      </header>

      {!loading && health && templates.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '12px 16px', marginBottom: 14, borderRadius: 12,
          background: needAttention ? '#fff' : '#f0fdf4',
          border: `1px solid ${needAttention ? COLORS.border : '#bbf7d0'}`,
        }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: needAttention ? COLORS.ink : '#166534' }}>
            {needAttention
              ? `${needAttention} active workflow${needAttention === 1 ? '' : 's'} need${needAttention === 1 ? 's' : ''} attention`
              : 'All active workflows are healthy'}
          </span>
          {totals.filter(t => t.n > 0).map(t => (
            <span key={t.kind} style={{
              fontSize: 12, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
              background: t.bg, color: t.fg,
            }}>
              {countLabel(t, t.n)}
            </span>
          ))}
          <label style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13, color: COLORS.inkSoft, cursor: 'pointer',
          }}>
            <input type="checkbox" checked={problemsOnly} onChange={e => setProblemsOnly(e.target.checked)} />
            Only show problems
          </label>
        </div>
      )}

      {loading ? (
        <div style={{ color: COLORS.inkMuted, padding: 60, textAlign: 'center' }}>Loading…</div>
      ) : templates.length === 0 ? (
        <div style={{
          padding: 40, borderRadius: 14,
          borderWidth: 1, borderStyle: 'dashed', borderColor: COLORS.border,
          textAlign: 'center', color: COLORS.inkMuted, fontSize: 14,
        }}>
          No workflows yet. Click <strong style={{ color: COLORS.ink }}>+ New Workflow</strong> to create your first one.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {shown.map(tmpl => (
            <Row
              key={tmpl.id}
              tmpl={tmpl}
              health={health ? health.get(tmpl.id) : null}
              onEdit={() => openEdit(tmpl)}
              onToggle={() => toggleActive(tmpl)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ tmpl, health, onEdit, onToggle }) {
  const [open, setOpen] = useState(false);
  const total = issueTotal(health);
  return (
    <div style={{
      borderRadius: 12, borderWidth: 1, borderStyle: 'solid', borderColor: COLORS.border,
      background: '#fff', opacity: tmpl.is_active ? 1 : 0.5, overflow: 'hidden',
    }}>
    <div
      onClick={onEdit}
      style={{
        padding: '14px 18px',
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        cursor: 'pointer', transition: 'background 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.brandTint; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: COLORS.ink }}>{tmpl.name}</span>
          {tmpl.is_default ? (
            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#dbeafe', color: '#1d4ed8', fontWeight: 700 }}>DEFAULT</span>
          ) : null}
          {!tmpl.is_active && (
            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: '#fee2e2', color: '#ef4444', fontWeight: 700 }}>INACTIVE</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: COLORS.inkMuted }}>
          {tmpl.module && <span style={{ marginRight: 12 }}>Module <strong style={{ color: COLORS.inkSoft }}>{tmpl.module}</strong></span>}
          {tmpl.section && <span style={{ marginRight: 12 }}>Section <strong style={{ color: COLORS.inkSoft }}>{tmpl.section}</strong></span>}
          {tmpl.trigger_source_type && <span style={{ marginRight: 12 }}>Trigger <strong style={{ color: COLORS.inkSoft }}>{tmpl.trigger_source_type}</strong></span>}
          <span>{tmpl.stage_count} stages · {tmpl.item_count} items</span>
        </div>
        {health && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
            {total === 0 ? (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#dcfce7', color: '#166534' }}>
                ✓ Healthy
              </span>
            ) : (
              <>
                {HEALTH_KINDS.filter(k => health.counts[k.kind] > 0).map(k => (
                  <span key={k.kind} style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: k.bg, color: k.fg }}>
                    {countLabel(k, health.counts[k.kind])}
                  </span>
                ))}
                <button type="button" onClick={() => setOpen(o => !o)}
                  style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                    border: `1px solid ${COLORS.border}`, background: '#fff', color: COLORS.inkSoft, cursor: 'pointer' }}>
                  {open ? 'Hide details' : 'Details'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onEdit}
          style={{ padding: '6px 12px', border: 'none',
            background: '#D4A843', color: '#1a1a2e',
            borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          Edit
        </button>
        <button type="button" onClick={onToggle}
          style={{ padding: '6px 10px',
            borderWidth: 1, borderStyle: 'solid',
            borderColor: tmpl.is_active ? '#fecaca' : '#bbf7d0',
            background: '#fff',
            color: tmpl.is_active ? '#b91c1c' : '#059669',
            borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          {tmpl.is_active ? 'Disable' : 'Enable'}
        </button>
      </div>
    </div>
    {open && health && total > 0 && <HealthDetails health={health} />}
    </div>
  );
}

// Issues grouped by step, in step order; workflow-wide issues first.
function HealthDetails({ health }) {
  const groups = new Map();
  for (const i of health.issues) {
    const key = i.stage_id || 0;
    if (!groups.has(key)) groups.set(key, { step: i.step, name: i.stage_name, items: [] });
    groups.get(key).items.push(i);
  }
  const ordered = [...groups.values()].sort((a, b) => (a.step || 0) - (b.step || 0));
  const style = Object.fromEntries(HEALTH_KINDS.map(k => [k.kind, k]));
  return (
    <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: '12px 18px 14px', background: '#f8fafc' }}>
      {health.module_driven && health.counts.path > 0 && (
        <p style={{ margin: '0 0 10px', fontSize: 12, color: COLORS.inkSoft }}>
          This module moves items between some steps automatically, so a
          "No step leads here" gap may be on purpose.
        </p>
      )}
      <div style={{ display: 'grid', gap: 6 }}>
        {ordered.map((g, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 13 }}>
            <span style={{ fontWeight: 700, color: COLORS.ink, minWidth: 180 }}>
              {g.step ? `Step ${g.step} · ${g.name}` : 'Whole workflow'}
            </span>
            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {g.items.map((i, j) => (
                <span key={j} style={{
                  fontSize: 12, padding: '2px 8px', borderRadius: 6,
                  background: style[i.kind].bg, color: style[i.kind].fg,
                }}>
                  {i.message}
                </span>
              ))}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
